import React, { useEffect, useCallback, useRef } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { useCreateConversation, useSendMessage, useGetConversationMessages, useSimpleChat } from '../utils/conversation.api';
import { useGetUserVehicles } from '../utils/vehicles.api';
import { useQueryClient } from '@tanstack/react-query';
import ChatLoadingDots from './ChatLoadingDots';
import VehicleDetailsForm from './VehicleDetailsForm';
import { useUIStore } from '../store/uiStore';
import { useVehicleStore } from '../store/vehicleStore';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';

const DashboardStateManager = ({ children }) => {
  // Zustand stores
  const {
    isSidebarOpen,
    isMobileDrawerOpen,
    isChatView,
    isSettingsView,
    isVehicleFormOpen,
    vehicleToEdit,
    toggleSidebar,
    toggleMobileDrawer,
    setIsSidebarOpen,
    setIsMobileDrawerOpen,
    setIsChatView,
    setIsSettingsView,
    openVehicleForm,
    closeVehicleForm,
    setVehicleToEdit,
  } = useUIStore();

  const {
    selectedVehicle,
    selectedVehicleConversationId,
    vehicleContext,
    setSelectedVehicle,
    setSelectedVehicleConversationId,
    setVehicleContext,
    clearSelectedVehicle,
    clearVehicleContext,
  } = useVehicleStore();

  const {
    inputValue,
    chatMessages,
    isLoading,
    currentConversationId,
    hasProcessedInitialState,
    hasAutoSelectedFirstChat,
    setInputValue,
    setChatMessages,
    setIsLoading,
    setCurrentConversationId,
    setHasProcessedInitialState,
    setHasAutoSelectedFirstChat,
    addChatMessage,
    clearChatMessages,
    getChatMessages,
  } = useChatStore();

  const {
    hasToken,
    setHasToken,
    initializeAuth,
    logout: authLogout,
  } = useAuthStore();
  
  // API hooks
  const createConversationMutation = useCreateConversation();
  const sendMessageMutation = useSendMessage();
  const simpleChatMutation = useSimpleChat();
  const queryClient = useQueryClient();
  // const getConversationMessagesQuery = useGetConversationMessages();
  
  // Get user vehicles for auto-selection
  const userId = localStorage.getItem('userid');
  const { data: vehicles } = useGetUserVehicles(userId);

  // Get conversation messages when a vehicle is selected
  const { data: conversationMessages, isLoading: messagesLoading, isFetching: messagesFetching, error: messagesError } = useGetConversationMessages(
    selectedVehicleConversationId
  );
  
  // Use isFetching when we have a conversationId but no data yet, or isLoading otherwise
  const isMessagesLoading = messagesLoading || (messagesFetching && !conversationMessages && selectedVehicleConversationId);
  
  console.log('Conversation messages query state:', {
    conversationId: selectedVehicleConversationId,
    data: conversationMessages,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
    isMessagesLoading,
    error: messagesError
  });

  // Responsive behavior - drawer on mobile, sidebar on desktop
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const showSidebar = !isMobile && isSidebarOpen;
  const showDrawer = isMobile && isMobileDrawerOpen;

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Check for flag to open vehicle form (from ServicesPage redirect)
  useEffect(() => {
    const shouldOpenForm = localStorage.getItem('openVehicleForm');
    if (shouldOpenForm === 'true') {
      localStorage.removeItem('openVehicleForm');
      // Small delay to ensure component is mounted
      setTimeout(() => {
        openVehicleForm();
      }, 300);
    }
  }, [openVehicleForm]);

  // Clear messages immediately when switching to a new conversation
  const previousConversationIdRef = useRef(null);
  useEffect(() => {
    if (!hasToken || !localStorage.getItem('token')) {
      return;
    }
    
    // If conversation ID changed, clear previous messages immediately
    if (selectedVehicleConversationId !== previousConversationIdRef.current && selectedVehicleConversationId) {
      console.log('Conversation changed, clearing previous messages:', selectedVehicleConversationId);
      setChatMessages([]);
      previousConversationIdRef.current = selectedVehicleConversationId;
    } else if (!selectedVehicleConversationId && previousConversationIdRef.current) {
      // If conversation ID is cleared, also clear messages
      console.log('Conversation cleared, clearing messages');
      setChatMessages([]);
      previousConversationIdRef.current = null;
    }
  }, [selectedVehicleConversationId, hasToken]);

  // Effect to update chatMessages when conversation messages are fetched
  // Only run this for logged-in users with a conversation ID
  useEffect(() => {
    // Skip this effect for non-logged-in users (they use simple_chat API and manage messages directly)
    if (!hasToken || !localStorage.getItem('token')) {
      console.log('Skipping conversation messages effect for non-logged-in user');
      return;
    }
    
    console.log('Raw conversation messages from API:', conversationMessages);
    console.log('Current isChatView state:', isChatView);
    console.log('Selected vehicle conversation ID:', selectedVehicleConversationId);
    
    // Update vehicle context from response if available
    const contextFromResponse = conversationMessages?.vehicle_context || conversationMessages?.vehicleContext || null;
    if (contextFromResponse !== undefined) {
      setVehicleContext(contextFromResponse);
    } else if (!conversationMessages) {
      setVehicleContext(null);
    }

    // Handle the API response structure where messages are nested
    let messagesArray = [];
    if (conversationMessages && conversationMessages.messages) {
      messagesArray = conversationMessages.messages;
    } else if (conversationMessages && Array.isArray(conversationMessages)) {
      messagesArray = conversationMessages;
    }
    
    console.log('Extracted messages array:', messagesArray);
    
    if (messagesArray && messagesArray.length > 0) {
      // Convert API response format to chatMessages format
      // Each API response item contains both user question and AI response
      const formattedMessages = [];
      
      messagesArray.forEach((msg, index) => {
        console.log('Processing message:', msg);
        
        // Add user message first (question)
        if (msg.user) {
          formattedMessages.push({
            id: `${msg.id}_user`,
            type: 'user',
            message: msg.user,
            timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            originalData: msg
          });
        }
        
        // Add AI response message (answer) with typing animation
        if (msg.agent) {
          formattedMessages.push({
            id: `${msg.id}_agent`,
            type: 'ai',
            message: msg.agent,
            timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            originalData: msg,
            rawText: msg.agent // Store raw text for reference
          });
        }
      });
      
      // Sort messages by timestamp to ensure proper chronological order
      formattedMessages.sort((a, b) => new Date(a.originalData.timestamp) - new Date(b.originalData.timestamp));
      
      setChatMessages(formattedMessages);
      console.log('Updated chatMessages with complete conversation history:', formattedMessages);
      
      // Always switch to chat view when we have messages
      setIsChatView(true);
      console.log('Switching to chat view due to conversation messages');
      
      // Add a small delay to ensure chat component is rendered before scrolling
      setTimeout(() => {
        // This will trigger the useEffect in ChatBox to scroll to bottom
        setChatMessages([...formattedMessages]);
      }, 200);
    } else if (messagesArray && messagesArray.length === 0) {
      // If no messages found, but we have a selected vehicle, switch to dashboard view
      // Only clear messages if we're actually fetching for a conversation ID
      if (selectedVehicleConversationId) {
        setChatMessages([]);
        // Vehicle has conversation but no messages - show dashboard with ChatInput
        setIsChatView(false);
        console.log('No conversation messages found, switching to dashboard view for selected vehicle');
      } else {
        // Vehicle has no conversation - stay in dashboard view to show ChatInput
        setIsChatView(false);
        console.log('Vehicle has no conversation, staying in dashboard view to show ChatInput');
      }
    } else if (conversationMessages === undefined && selectedVehicleConversationId) {
      // If conversationMessages is undefined but we have a selected vehicle,
      // it means the API call is still loading or hasn't started yet
      // Stay in dashboard view until we know the result
      console.log('Conversation messages still loading, staying in dashboard view');
    } else if (conversationMessages === undefined && !selectedVehicleConversationId && selectedVehicle) {
      // Vehicle has no conversation - stay in dashboard view to show ChatInput
      console.log('Vehicle has no conversation, staying in dashboard view to show ChatInput');
      setIsChatView(false);
    }
  }, [conversationMessages, isChatView, selectedVehicleConversationId, hasToken]);
  
  // Effect to maintain chat view when a vehicle is selected
  useEffect(() => {
    if (selectedVehicle) {
      if (selectedVehicleConversationId && chatMessages && chatMessages.length > 0) {
        // Vehicle has conversation and messages - switch to chat view
        console.log('Vehicle selected with chat messages, switching to chat view');
        setIsChatView(true);
      } else if (selectedVehicleConversationId && chatMessages && chatMessages.length === 0) {
        // Vehicle has conversation but no messages - stay in dashboard view
        console.log('Vehicle selected with conversation but no messages, staying in dashboard view');
        setIsChatView(false);
      } else if (!selectedVehicleConversationId) {
        // Vehicle has no conversation - stay in dashboard view to show ChatInput
        console.log('Vehicle selected with no conversation, staying in dashboard view to show ChatInput');
        setIsChatView(false);
      }
    }
  }, [selectedVehicle, selectedVehicleConversationId, chatMessages, isChatView]);
  
  // Check for token in localStorage on component mount
  useEffect(() => {
    // Immediately check for token on mount
    const checkToken = () => {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      console.log('Checking token on mount:', { token: !!token, username });
      setHasToken(!!token);
    };

    // Check immediately
    checkToken();

    // Listen for localStorage changes
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        console.log('Token changed in storage:', { newValue: !!e.newValue });
        setHasToken(!!e.newValue);
      }
    };

    // Listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      checkToken();
    };

    // Listen for conversation refresh events
    const handleConversationRefresh = (event) => {
      const { conversationId, vehicleId } = event.detail;
      console.log('🔄 Conversation refresh triggered:', { conversationId, vehicleId });
      
      // Invalidate conversation messages to trigger refetch
      queryClient.invalidateQueries(['conversations', conversationId, 'messages']);
      
      // Invalidate vehicles list to get updated conversation_id
      queryClient.invalidateQueries(['vehicles', 'user']);
      
      // If this is the currently selected vehicle, refresh its data
      if (selectedVehicle && selectedVehicle.id === vehicleId) {
        console.log('🔄 Refreshing currently selected vehicle data');
        queryClient.invalidateQueries(['vehicles', vehicleId]);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    window.addEventListener('conversationRefresh', handleConversationRefresh);
    
    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
      window.removeEventListener('conversationRefresh', handleConversationRefresh);
    };
  }, []);

  // Clear chat-related state whenever the user logs out
  useEffect(() => {
    if (hasToken) {
      return;
    }

    clearSelectedVehicle();
    clearVehicleContext();
    clearChatMessages();
    setIsChatView(false);
    setCurrentConversationId(null);
    setHasProcessedInitialState(false);
    setHasAutoSelectedFirstChat(false);
  }, [
    hasToken,
    clearSelectedVehicle,
    clearVehicleContext,
    clearChatMessages,
    setIsChatView,
    setCurrentConversationId,
    setHasProcessedInitialState,
    setHasAutoSelectedFirstChat,
  ]);

  // Handle vehicle selection from sidebar
  const handleVehicleSelect = useCallback((vehicle) => {
    if (vehicle === null) {
      // Clear selection
      clearSelectedVehicle();
      clearChatMessages(); // Clear chat messages when no vehicle is selected
      clearVehicleContext();
      setIsChatView(false); // Ensure we're back to dashboard view
      setHasProcessedInitialState(false); // Reset the flag
      setHasAutoSelectedFirstChat(false); // Reset auto-selection flag
      console.log('Vehicle selection cleared');
    } else {
      // Check if vehicle has conversation_id
      if (!vehicle.conversation_id) {
        console.error('Vehicle missing conversation_id:', vehicle);
        // Create a temporary conversation ID or handle this case
        // For now, we'll still show the chat view but with a warning
      }
      
      // Set selected vehicle
      setSelectedVehicle(vehicle);
      // Clear previous chat messages while loading new ones
      clearChatMessages();
      clearVehicleContext();
      setHasProcessedInitialState(false); // Reset the flag to process new vehicle
      
      // If this is a manual selection (not auto-selection), mark that auto-selection is complete
      if (hasAutoSelectedFirstChat) {
        console.log('Manual vehicle selection detected, auto-selection is now complete');
      }
      
      // If vehicle has no conversation, immediately show dashboard view
      if (!vehicle.conversation_id) {
        console.log('Vehicle has no conversation, showing dashboard view with ChatInput');
        setIsChatView(false);
      } else if (isChatView) {
        // If we're currently in chat view, stay there until we know if there are messages
        console.log('Staying in chat view while loading vehicle messages');
      } else {
        console.log('Vehicle selected, waiting for messages to determine view');
      }
      
      console.log('Selected vehicle:', vehicle);
      console.log('Selected vehicle conversation ID:', vehicle.conversation_id);
    }
  }, [hasAutoSelectedFirstChat, isChatView, setSelectedVehicle, clearSelectedVehicle, clearChatMessages, clearVehicleContext, setIsChatView, setHasProcessedInitialState, setHasAutoSelectedFirstChat]);

  // Auto-select first vehicle with conversation when dashboard loads
  useEffect(() => {
    // Check if we need to auto-select a newly created vehicle
    const autoSelectNewVehicle = localStorage.getItem('autoSelectNewVehicle');
    const newlyCreatedVehicle = localStorage.getItem('newlyCreatedVehicle');
    
    if (autoSelectNewVehicle === 'true' && newlyCreatedVehicle) {
      try {
        const newVehicle = JSON.parse(newlyCreatedVehicle);
        console.log('🚗 Auto-selecting newly created vehicle:', newVehicle);
        
        // Clean up the localStorage flags
        localStorage.removeItem('autoSelectNewVehicle');
        localStorage.removeItem('newlyCreatedVehicle');
        
        // Auto-select the new vehicle
        setHasAutoSelectedFirstChat(true);
        handleVehicleSelect(newVehicle);
        return; // Exit early to prevent normal auto-selection
      } catch (error) {
        console.error('Failed to parse newly created vehicle:', error);
        // Clean up invalid data
        localStorage.removeItem('autoSelectNewVehicle');
        localStorage.removeItem('newlyCreatedVehicle');
      }
    }
    
    // Only auto-select if:
    // 1. We haven't already auto-selected a chat
    // 2. We have vehicles loaded
    // 3. No vehicle is currently selected
    // 4. User is logged in
    if (!hasAutoSelectedFirstChat && vehicles && vehicles.length > 0 && !selectedVehicle && hasToken) {
      // Find the first vehicle that has a conversation_id
      const firstVehicleWithChat = vehicles.find(vehicle => vehicle.conversation_id);
      
      if (firstVehicleWithChat) {
        console.log('Auto-selecting first vehicle with chat:', firstVehicleWithChat);
        setHasAutoSelectedFirstChat(true);
        handleVehicleSelect(firstVehicleWithChat);
      }
    }
  }, [vehicles, hasAutoSelectedFirstChat, selectedVehicle, hasToken, handleVehicleSelect, setHasAutoSelectedFirstChat]);

  // Handle logout
  const handleLogout = () => {
    authLogout(); // This already handles localStorage.clear(), setHasToken, and reload
  };

  // Handle settings click - navigate to settings and close sidebar/drawer
  const handleSettingsClick = () => {
    setIsSettingsView(true);
    if (isMobile) {
      setIsMobileDrawerOpen(false); // Close mobile drawer
    } else {
      setIsSidebarOpen(false); // Close desktop sidebar
    }
  };

  // Handle going back to dashboard
  const handleBackToDashboard = () => {
    setIsChatView(false);
    setCurrentConversationId(null);
    clearSelectedVehicle();
    clearChatMessages(); // Clear chat messages instead of setting hardcoded message
    setHasProcessedInitialState(false); // Reset the flag
    setHasAutoSelectedFirstChat(false); // Reset auto-selection flag to allow re-selection
    console.log('Returned to dashboard view');
  };

  // Handle going back to settings
  const handleBackToSettings = () => {
    setIsSettingsView(false);
  };

  // Function to extract first 2 sentences from text
  const extractFirstTwoSentences = (text) => {
    const sentences = text.match(/[^.!?]+[.!?]+/g);
    if (sentences && sentences.length >= 2) {
      return sentences.slice(0, 2).join(' ').trim();
    }
    return text.trim();
  };

  // Handle chat submission
  const handleChatSubmit = async (text, messageType = "text") => {
    if (!text.trim() || isLoading) return;

    setIsLoading(true);

    // Check if user is logged in
    const isLoggedIn = hasToken && localStorage.getItem('token');

    try {
      // Add user message to chat immediately
      const userMessage = {
        id: Date.now(),
        type: 'user',
        message: text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      addChatMessage(userMessage);

      // Add loading message
      const loadingMessage = {
        id: Date.now() + 1,
        type: 'ai',
        message: <ChatLoadingDots size="md" color="gray.500" />,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isLoading: true
      };
      
      addChatMessage(loadingMessage);

      // If user is not logged in, use simple_chat API
      if (!isLoggedIn) {
        console.log('User not logged in, using simple_chat API');
        console.log('Current chatMessages before API call:', chatMessages);
        
        // Switch to chat view immediately so ChatBox is ready to render messages
        setIsChatView(true);

        try {
          const simpleChatResponse = await simpleChatMutation.mutateAsync(text);
          console.log('Simple chat API response:', simpleChatResponse);
          
          // Update messages: remove loading and add AI response in one atomic update
          const currentMessages = getChatMessages();
          console.log('Updating chatMessages, previous state:', currentMessages);
          const filtered = currentMessages.filter(msg => !msg.isLoading);
          const newMessages = [...filtered];
          
          if (simpleChatResponse && simpleChatResponse.response) {
            const aiMessage = {
              id: Date.now() + 2,
              type: 'ai',
              message: simpleChatResponse.response,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              rawText: simpleChatResponse.response
            };
            console.log('Adding AI message to chat:', aiMessage);
            newMessages.push(aiMessage);
          } else {
            // Fallback message if no response received
            const aiMessage = {
              id: Date.now() + 2,
              type: 'ai',
              message: "Thank you for your message. How can I help you further?",
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              rawText: "Thank you for your message. How can I help you further?"
            };
            console.log('Adding fallback message to chat:', aiMessage);
            newMessages.push(aiMessage);
          }
          
          console.log('Updated chatMessages for non-logged-in user:', newMessages);
          setChatMessages(newMessages);
          
        } catch (simpleChatError) {
          console.error('Error in simple chat:', simpleChatError);
          
          // Update messages: remove loading and add error message
          const currentMessages = getChatMessages();
          const filtered = currentMessages.filter(msg => !msg.isLoading);
          const errorMessage = {
            id: Date.now() + 2,
            type: 'ai',
            message: "Sorry, there was an error processing your request. Please try again.",
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawText: "Sorry, there was an error processing your request. Please try again."
          };
          console.log('Adding error message to chat:', errorMessage);
          setChatMessages([...filtered, errorMessage]);
        }
        
        setIsLoading(false);
        return; // Exit early for non-logged-in users
      }

      // Determine which conversation ID to use (for logged-in users)
      const targetConversationId = selectedVehicleConversationId || currentConversationId;

      // If we don't have a conversation ID, create one
      if (!targetConversationId) {
        // Extract first 2 sentences for conversation name
        const conversationName = extractFirstTwoSentences(text);
        
        // Create conversation - use vehicle_id if a vehicle is selected
        const conversationData = {
          conversation_name: conversationName,
          vehicle_id: selectedVehicle ? selectedVehicle.id : null,
          conversation_type: selectedVehicle ? "vehicle_specific" : "general",
          user_id: null
        };

        const conversationResponse = await createConversationMutation.mutateAsync(conversationData);
        
        if (conversationResponse && conversationResponse.id) {
          const conversationId = conversationResponse.id;
          
          // If this is for a selected vehicle, update the selectedVehicleConversationId
          if (selectedVehicle) {
            setSelectedVehicleConversationId(conversationId);
            console.log('Created new conversation for vehicle:', conversationId);
          } else {
            setCurrentConversationId(conversationId);
          }
          
          // Set chat view to true
          setIsChatView(true);
          
          // Send message to conversation using the conversationId from response
          const messageData = {
            message: text,
            message_type: messageType,
            metadata: null,
            user_id: null
          };
          
          console.log('Sending message with data:', messageData);

          const messageResponse = await sendMessageMutation.mutateAsync({
            conversationId: conversationId, // Use the ID from response directly
            messageData
          });

          // Remove loading message and add AI response with typing animation
          const currentMessages = getChatMessages();
          setChatMessages(currentMessages.filter(msg => !msg.isLoading));

          if (messageResponse && messageResponse.response) {
            const aiMessage = {
              id: Date.now() + 2,
              type: 'ai',
              message: messageResponse.response,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              rawText: messageResponse.response // Store raw text for reference
            };
            console.log('AI response message:', aiMessage);
            addChatMessage(aiMessage);
          } else {
            // Fallback message if no response received
            const aiMessage = {
              id: Date.now() + 2,
              type: 'ai',
              message: "Thank you for your message. How can I help you further?",
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              rawText: "Thank you for your message. How can I help you further?"
            };
            
            addChatMessage(aiMessage);
          }
        } else {
          throw new Error('Failed to create conversation');
        }
      } else {
        // If we already have a conversation ID, just send the message
        const messageData = {
          message: text,
          message_type: messageType,
          metadata: null,
          user_id: null
        };
        
        console.log('Sending message with data:', messageData);

        const messageResponse = await sendMessageMutation.mutateAsync({
          conversationId: targetConversationId,
          messageData
        });

        // Remove loading message and add AI response with typing animation
        const currentMessages = getChatMessages();
        setChatMessages(currentMessages.filter(msg => !msg.isLoading));

        if (messageResponse && messageResponse.response) {
          const aiMessage = {
            id: Date.now() + 2,
            type: 'ai',
            message: messageResponse.response,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawText: messageResponse.response // Store raw text for reference
          };
          
          addChatMessage(aiMessage);
        } else {
          // Fallback message if no response received
          const aiMessage = {
            id: Date.now() + 2,
            type: 'ai',
            message: "Thank you for your message. How can I help you further?",
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            rawText: "Thank you for your message. How can I help you further?"
          };
          
          addChatMessage(aiMessage);
        }
      }
    } catch (error) {
      console.error('Error in chat submission:', error);
      
      // Remove loading message
      const currentMessages = getChatMessages();
      setChatMessages(currentMessages.filter(msg => !msg.isLoading));
      
      // Add error message to chat with typing animation
      const errorMessage = {
        id: Date.now() + 2,
        type: 'ai',
        message: "Sorry, there was an error processing your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        rawText: "Sorry, there was an error processing your request. Please try again."
      };
      addChatMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear chat - clear local chat messages when conversation is deleted
  const handleClearChat = (conversationId) => {
    // Clear the local chat messages state
    clearChatMessages();
    clearVehicleContext();
    
    // If this is the currently selected conversation, switch to dashboard view
    if (selectedVehicleConversationId === conversationId) {
      setIsChatView(false);
    }
  };

  return (
    <>
      {children({
        // State values
        inputValue,
        isSidebarOpen,
        isChatView,
        isSettingsView,
        hasToken,
        chatMessages,
        currentConversationId,
        selectedVehicleConversationId,
        selectedVehicle,
        isLoading,
        messagesLoading: isMessagesLoading,
        isMobile,
        showSidebar,
        showDrawer,
        vehicleContext,
        hasProcessedInitialState,
        hasAutoSelectedFirstChat,
        
        // State setters
        setInputValue,
        setIsSidebarOpen,
        setIsChatView,
        setIsSettingsView,
        setChatMessages,
        setCurrentConversationId,
        setSelectedVehicleConversationId,
        setSelectedVehicle,
        setIsLoading,
        setVehicleContext,
        
        // Actions
        handleVehicleSelect,
        handleLogout,
        handleSettingsClick,
        handleBackToDashboard,
        handleBackToSettings,
        handleChatSubmit,
        handleClearChat,
        toggleSidebar,
        toggleMobileDrawer,
        
        // Vehicle form modal
        isVehicleFormOpen,
        onVehicleFormOpen: openVehicleForm,
        onVehicleFormClose: closeVehicleForm,
        vehicleToEdit,
        setVehicleToEdit
      })}
      {/* Vehicle Details Form Modal - Always accessible */}
      <VehicleDetailsForm
        isOpen={isVehicleFormOpen}
        onClose={() => {
          closeVehicleForm();
          setVehicleToEdit(null);
        }}
        vehicleToEdit={vehicleToEdit}
        onEditComplete={() => {
          setVehicleToEdit(null);
          // Refresh vehicles list if needed
          const userId = localStorage.getItem('userid');
          if (userId) {
            queryClient.invalidateQueries(['vehicles', 'user', userId]);
          }
        }}
      />
    </>
  );
};

export default DashboardStateManager; 